import React from 'react';

/**
 * Primary navigation rail — warm-cream + sage redesign.
 *
 * A floating, full-height column (224px ⇆ 56px collapsed) inset 14px from the
 * viewport edges, lifted off a faint blueprint grid (the grid lives in App.css
 * on `.app`) with a soft elevation shadow and a faint procedural MEP/HVAC
 * drawing watermark behind the rail itself.
 *
 * Anatomy: Brand block · grouped nav (Overview / Operations / Finance /
 * Administration, scrolls) · user card (avatar + name/role + theme & sign-out).
 *
 * Visual + organizational redesign only — the prop contract, routing,
 * role-gating (upstream in App.js), collapse, dark-mode toggle and logout are
 * unchanged. A group whose items are all role-hidden renders nothing; any nav
 * item not assigned to a group falls into a trailing un-labeled section so a
 * future item can never silently disappear.
 */

const SIDEBAR_WIDTH = 224;
const SIDEBAR_WIDTH_COLLAPSED = 56;

/* nav key → icon (real App.js keys) */
const NAV_ICON = {
  projects: 'dashboard',
  notifications: 'bell',
  'ce-activity': 'activity',
  'pre-construction': 'hardhat',
  workload: 'calendar',
  'schedule-log': 'clock',
  ar: 'wallet',
  'billings-projection': 'trending-up',
  'job-costs': 'receipt',
  'dept-status': 'layers',
  'audit-log': 'shield',
  'user-management': 'team',
  settings: 'gear',
};

/* grouped nav (real App.js keys) */
const NAV_GROUPS = [
  { label: 'Overview', keys: ['projects', 'notifications'] },
  { label: 'Operations', keys: ['ce-activity', 'pre-construction', 'workload', 'schedule-log'] },
  { label: 'Finance', keys: ['ar', 'billings-projection', 'job-costs', 'dept-status'] },
  { label: 'Administration', keys: ['audit-log', 'user-management', 'settings'] },
];

/* project-detail tab key → icon (this mode isn't mounted today — the sidebar is
   hidden on /projects/:id/* — but kept correct + safe for completeness) */
const TAB_ICON = {
  dashboard: 'dashboard', sov: 'grid', tasks: 'calendar', rollup: 'layers',
  billing: 'wallet', performance: 'activity', forecast: 'trending-up',
  'contract value': 'doc', 'job-costs': 'receipt', 'client view': 'eye',
};

/* -------------------- inline-SVG icon set -------------------- */
const SbIcon = ({ name, size = 17, sw = 1.6 }) => {
  const p = (d) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>{d}</svg>
  );
  switch (name) {
    case 'dashboard':     return p(<><rect x="3" y="3" width="7.5" height="7.5" rx="1.4" /><rect x="13.5" y="3" width="7.5" height="7.5" rx="1.4" /><rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.4" /><rect x="3" y="13.5" width="7.5" height="7.5" rx="1.4" /></>);
    case 'bell':          return p(<><path d="M6 8.5a6 6 0 0 1 12 0c0 4 1.4 4.9 2 6H4c.6-1.1 2-2 2-6Z" /><path d="M10 19.5a2 2 0 0 0 4 0" /></>);
    case 'activity':      return p(<path d="M3 12.5h3.5l2.2 6 3.6-13 2.4 7h4.3" />);
    case 'hardhat':       return p(<><path d="M2.5 18h19" /><path d="M5 18a7 7 0 0 1 14 0" /><path d="M12 4.2V7M9.4 7.4 9.6 6A1.2 1.2 0 0 1 10.8 5h2.4a1.2 1.2 0 0 1 1.2 1l.2 1.4" /></>);
    case 'calendar':      return p(<><rect x="3" y="4.5" width="18" height="16.5" rx="2" /><path d="M3 9h18M8 2.5v4M16 2.5v4" /></>);
    case 'clock':         return p(<><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3.2 1.9" /></>);
    case 'wallet':        return p(<><path d="M4 7.5a2 2 0 0 1 2-2h10.5a1.5 1.5 0 0 1 1.5 1.5v1" /><rect x="3" y="7.5" width="18" height="12" rx="2.2" /><circle cx="16.5" cy="13.5" r="1.25" fill="currentColor" stroke="none" /></>);
    case 'trending-up':   return p(<><path d="M3 17.5 9.5 11l4 4L21 7.5" /><path d="M15.5 7.5H21v5.5" /></>);
    case 'receipt':       return p(<><path d="M5.5 21V4.2a1 1 0 0 1 1-1h11a1 1 0 0 1 1 1V21l-2.4-1.5L14.7 21l-2.7-1.5L9.3 21l-2.6-1.5z" /><path d="M9 8h6M9 11.6h6M9 15.2h3.5" /></>);
    case 'shield':        return p(<><path d="M12 3 5 6v5c0 4 3 7 7 9 4-2 7-5 7-9V6Z" /><path d="m9.4 12 1.9 1.9L15 10" /></>);
    case 'team':          return p(<><path d="M16.5 19a4.5 4.5 0 0 0-9 0" /><circle cx="12" cy="8.5" r="3.2" /><path d="M4.5 18.5a3.2 3.2 0 0 1 3.7-3.4M19.5 18.5a3.2 3.2 0 0 0-3.7-3.4" /></>);
    case 'gear':          return p(<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-2.82 1.17V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 8 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15H4.5a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 6 9.4a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 11 4.6V4.5a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 2.82 1.17l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 21.4 11H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" /></>);
    case 'sun':           return p(<><circle cx="12" cy="12" r="4" /><path d="M12 2.5v2.2M12 19.3v2.2M4.6 4.6l1.6 1.6M17.8 17.8l1.6 1.6M2.5 12h2.2M19.3 12h2.2M4.6 19.4l1.6-1.6M17.8 6.2l1.6-1.6" /></>);
    case 'moon':          return p(<path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8Z" />);
    case 'logout':        return p(<><path d="M9 21H5.5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2H9" /><path d="m16 16.5 4.5-4.5L16 7.5" /><path d="M20.5 12H9.5" /></>);
    case 'chevrons-left': return p(<path d="M11 6.5 5.5 12 11 17.5M18.5 6.5 13 12l5.5 5.5" />);
    case 'chevrons-right':return p(<path d="M13 6.5 18.5 12 13 17.5M5.5 6.5 11 12l-5.5 5.5" />);
    case 'arrow-left':    return p(<><path d="M20 12H5" /><path d="m11 6-6 6 6 6" /></>);
    case 'grid':          return p(<><path d="M3 9.5h18M9.5 21V9.5" /><rect x="3" y="4" width="18" height="16.5" rx="2" /></>);
    case 'doc':           return p(<><path d="M14 3H6.5a1.5 1.5 0 0 0-1.5 1.5v15A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5V8z" /><path d="M14 3v5h5M8.5 13h7M8.5 16.5h7" /></>);
    case 'layers':        return p(<><path d="m12 3 9 5-9 5-9-5 9-5Z" /><path d="m3 13 9 5 9-5M3 16l9 5 9-5" opacity=".55" /></>);
    case 'eye':           return p(<><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="3" /></>);
    default: return null;
  }
};

/* -------------------- local brand + avatar primitives -------------------- */
const Logo = ({ size = 32, radius = 9 }) => (
  <span style={{
    width: size, height: size, borderRadius: radius, flex: '0 0 auto',
    background: 'linear-gradient(150deg, var(--accent), oklch(0.41 0.06 148))',
    color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'var(--font-mono)', fontSize: size * 0.4, fontWeight: 700, letterSpacing: '-0.03em',
    boxShadow: '0 1px 2px oklch(0.4 0.05 135 / 0.35), inset 0 1px 0 oklch(0.9 0.05 135 / 0.25)',
  }}>E3</span>
);

const Avatar = ({ first, last, size = 32 }) => {
  const ini = `${(first || '?')[0] || ''}${(last || '')[0] || ''}`.toUpperCase();
  return (
    <span style={{
      width: size, height: size, borderRadius: size * 0.3, flex: '0 0 auto',
      background: 'var(--accent-soft)', color: 'var(--accent-text)',
      border: '1px solid color-mix(in oklch, var(--accent) 22%, transparent)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--font-mono)', fontSize: size * 0.36, fontWeight: 600, letterSpacing: '-0.02em',
    }}>{ini}</span>
  );
};

const ROLE_LABEL = {
  admin: 'Admin', vp: 'Vice President',
  construction_director: 'Const. Director', construction_manager: 'Const. Manager',
  pm: 'Project Manager',
};

/* -------------------- one-time style injection (hover + MEP watermark) ----- */
function ensureStyles() {
  if (typeof document === 'undefined' || document.getElementById('sb-styles')) return;

  // Faint MEP/HVAC plan watermark, drawn only behind the rail — duct trunks +
  // branches, elbows, diffuser boxes, coil-hatched equipment, tag pills and
  // dimension strings. Seeded PRNG (no Math.random) → stable across reloads.
  const mepPlan = (c, o) => {
    let s = 0x6d2b79f5 >>> 0;
    const rnd = () => { s = (s + 0x6D2B79F5) >>> 0; let t = Math.imul(s ^ (s >>> 15), 1 | s); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
    const ri = (a, b) => Math.floor(a + rnd() * (b - a + 1));
    const pick = (arr) => arr[ri(0, arr.length - 1)];
    const n1 = (v) => Math.round(v * 10) / 10;
    let g = '';
    const P = (d, e) => { g += `<path d='${d}'${e ? ' ' + e : ''}/>`; };
    const L = (x1, y1, x2, y2, e) => P(`M${n1(x1)} ${n1(y1)}L${n1(x2)} ${n1(y2)}`, e);
    const R = (x, y, w, h, e) => { g += `<rect x='${n1(x)}' y='${n1(y)}' width='${n1(w)}' height='${n1(h)}'${e ? ' ' + e : ''}/>`; };
    const C = (x, y, r, e) => { g += `<circle cx='${n1(x)}' cy='${n1(y)}' r='${n1(r)}'${e ? ' ' + e : ''}/>`; };
    const T = (x, y, str, sz, rot) => { const tr = rot ? ` transform='rotate(${rot} ${n1(x)} ${n1(y)})'` : ''; g += `<text x='${n1(x)}' y='${n1(y)}' font-size='${sz || 5}' fill='${c}' fill-opacity='${o}' stroke='none' font-family='ui-monospace,monospace'${tr}>${str}</text>`; };
    const SIZES = ['900x500', '600x400', '1050x250', '450x450', '300x300', '750x350', '1200x600', '250x150', '400x200', '800x400', '200dia', '315dia'];
    const FLOWS = ['550 L/s', '340 L/s', '1130 L/s', '275 L/s', '910 L/s'];
    const TAGS = ['AHU-L7-04', 'VAV-12', 'FCU-7', 'EF-3', 'SA-15', 'MVCD-L7', 'PEF-L2-06', 'MFD-L7', 'RA-2', 'SD-9', 'CHW-3', 'PU-W-05'];
    const ductH = (x1, x2, y, w, dash) => { L(x1, y, x2, y); L(x1, y + w, x2, y + w); if (dash) L(x1, y + w / 2, x2, y + w / 2, "stroke-dasharray='1 5'"); };
    const ductV = (y1, y2, x, w, dash) => { L(x, y1, x, y2); L(x + w, y1, x + w, y2); if (dash) L(x + w / 2, y1, x + w / 2, y2, "stroke-dasharray='1 5'"); };
    const diff = (x, y, sx, sy) => { R(x, y, sx, sy); L(x, y, x + sx, y + sy); L(x + sx, y, x, y + sy); };
    const tag = (x, y, str) => { const w = str.length * 3.0 + 5; R(x, y, n1(w), 8, "rx='1.5'"); T(x + 2.5, y + 5.7, str, 4.6); };
    const dim = (x1, x2, y, str) => { L(x1, y, x2, y); L(x1, y - 2.4, x1, y + 2.4); L(x2, y - 2.4, x2, y + 2.4); if (str) T((x1 + x2) / 2 - str.length * 1.35, y - 1.8, str, 4.4); };
    const diag = (x, y, len, w, sign) => { const d = len * 0.7071, a = sign > 0 ? 1 : -1; L(x, y, x + d, y + d * a); L(x, y + w, x + d, y + d * a + w); };
    const ahu = (x, y, w, h) => { R(x, y, w, h); const n = Math.max(3, Math.round(w / 6)); for (let i = 1; i < n; i++) L(x + w * i / n, y + 2, x + w * i / n, y + h - 2); L(x, y + h / 2, x + w, y + h / 2, "stroke-dasharray='1 3'"); tag(x, y - 10, pick(TAGS)); };
    const hatch = (x, y, w, h) => { R(x, y, w, h); for (let i = 4; i < w + h; i += 5) { const x1 = x + Math.max(0, i - h), y1 = y + Math.min(h, i), x2 = x + Math.min(w, i), y2 = y + Math.max(0, i - w); L(x1, y1, x2, y2); } };
    const bubble = (x, y, str) => { C(x, y, 7); T(x - str.length * 1.3, y + 2, str, 5); };
    const H = 1000;
    let y = 26;
    while (y < H - 30) {
      const x1 = ri(-12, 26), x2 = ri(150, 236), w = ri(8, 22);
      ductH(x1, x2, y, w, rnd() > 0.45);
      if (x2 < 224) { L(x2, y, x2, y + w); if (rnd() > 0.5) diag(x2, y, ri(14, 26), w, rnd() > 0.5 ? 1 : -1); }
      if (rnd() > 0.3) T(ri(x1 + 12, Math.max(x1 + 13, x2 - 34)), y - 2.4, pick(SIZES), 4.8);
      if (rnd() > 0.32) {
        const bx = ri(Math.round(x1) + 24, Math.round(x2) - 26), bw = ri(6, Math.max(7, w - 2));
        const dir = rnd() > 0.5 ? 1 : -1, by2 = y + dir * ri(34, 110);
        ductV(Math.min(y, by2), Math.max(y, by2), bx, bw, rnd() > 0.6);
        if (rnd() > 0.45) diff(bx - 1, by2 + (dir > 0 ? -bw - 2 : 1), bw + 2, bw + 2);
      }
      y += ri(58, 104);
    }
    for (let i = 0; i < 16; i++) { const sx = ri(7, 18), sy = ri(7, 16); diff(ri(4, 200), ri(20, H - 24), sx, sy); }
    for (let i = 0; i < 7; i++) ahu(ri(8, 150), ri(40, H - 50), ri(36, 64), ri(20, 36));
    for (let i = 0; i < 4; i++) hatch(ri(8, 150), ri(30, H - 60), ri(24, 50), ri(18, 40));
    for (let i = 0; i < 9; i++) tag(ri(6, 150), ri(20, H - 16), pick(rnd() > 0.5 ? TAGS : FLOWS));
    for (let i = 0; i < 13; i++) { const x = ri(8, 120), len = ri(36, 120); dim(x, x + len, ri(20, H - 14), pick(SIZES)); }
    for (let i = 0; i < 8; i++) { const x = ri(14, 206), yy = ri(20, H - 18); bubble(x, yy, pick(['A', 'B', 'C', '1', '2', '3', '4'])); if (rnd() > 0.5) L(x, yy + 7, x, yy + ri(14, 30), "stroke-dasharray='2 4'"); }
    for (let i = 0; i < 6; i++) diag(ri(20, 170), ri(30, H - 40), ri(20, 40), ri(7, 16), rnd() > 0.5 ? 1 : -1);
    for (let i = 0; i < 3; i++) { const yy = ri(40, H - 40); L(-10, yy, 234, yy, "stroke-dasharray='6 4 1 4'"); }
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='224' height='${H}' viewBox='0 0 224 ${H}' fill='none' stroke='${c}' stroke-opacity='${o}' stroke-width='0.8' stroke-linecap='round' stroke-linejoin='round'>${g}</svg>`;
    return 'url("data:image/svg+xml,' + encodeURIComponent(svg) + '")';
  };
  const RAIL_ART = mepPlan('rgb(38,64,108)', 0.07);
  const RAIL_ART_DARK = mepPlan('rgb(150,182,228)', 0.085);

  const el = document.createElement('style');
  el.id = 'sb-styles';
  el.textContent = `
    .sb-item{ transition: background 110ms ease, color 110ms ease; }
    .sb-item:not(.sb-on):hover{ background: var(--bg-hover); color: var(--text-1); }
    .sb-item:not(.sb-on):hover .sb-ico{ color: var(--text-2); }
    .sb-iconbtn{ transition: background 110ms ease, color 110ms ease, border-color 110ms ease; }
    .sb-iconbtn:hover{ background: var(--bg-hover); color: var(--text-1); border-color: var(--border-default); }
    .sb-iconbtn.sb-danger:hover{ background: var(--danger-soft); color: var(--danger); border-color: color-mix(in oklch, var(--danger) 24%, transparent); }
    .sb-brandbtn{ transition: background 110ms ease; }
    .sb-brandbtn:hover{ background: var(--bg-hover); }
    .sb-back:hover{ background: var(--bg-hover); color: var(--text-1); }
    .sb-nav::-webkit-scrollbar{ width: 0; }
    .sb-nav{ scrollbar-width: none; }
    .sb-rail-float{ box-shadow: 0 1px 2px oklch(0.4 0.03 150 / 0.05), 0 8px 24px -10px oklch(0.35 0.03 150 / 0.22), 0 2px 8px -4px oklch(0.35 0.03 150 / 0.12); }
    .app.dark .sb-rail-float, .theme-dark .sb-rail-float{ box-shadow: 0 1px 2px oklch(0 0 0 / 0.3), 0 12px 32px -12px oklch(0 0 0 / 0.55); }
    .sb-rail{ background-color: var(--bg-sidebar); background-image: ${RAIL_ART}; background-repeat: no-repeat; background-position: center top; background-size: 224px auto; }
    .app.dark .sb-rail, .theme-dark .sb-rail{ background-image: ${RAIL_ART_DARK}; }
  `;
  document.head.appendChild(el);
}

const RING = '1px solid color-mix(in oklch, var(--accent) 22%, transparent)';

/* -------------------- nav row -------------------- */
function NavItem({ icon, label, path, active, badge, collapsed, onNavigate }) {
  if (collapsed) {
    return (
      <button type="button" className={'sb-item' + (active ? ' sb-on' : '')} title={label}
        onClick={() => onNavigate && onNavigate(path)}
        style={{
          position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 40, height: 40, margin: '0 auto', borderRadius: 9, cursor: 'pointer',
          color: active ? 'var(--accent-text)' : 'var(--text-2)',
          background: active ? 'var(--accent-soft)' : 'transparent',
          border: active ? RING : '1px solid transparent',
        }}>
        {active && <span style={{ position: 'absolute', left: -8, top: 11, bottom: 11, width: 3, borderRadius: '0 3px 3px 0', background: 'var(--accent)' }} />}
        <span className="sb-ico" style={{ display: 'inline-flex' }}><SbIcon name={icon} size={18} /></span>
        {badge > 0 && <span style={{ position: 'absolute', top: 6, right: 6, width: 7, height: 7, borderRadius: 99, background: 'var(--accent)', border: '1.5px solid var(--bg-sidebar)' }} />}
      </button>
    );
  }
  return (
    <button type="button" className={'sb-item' + (active ? ' sb-on' : '')}
      onClick={() => onNavigate && onNavigate(path)}
      style={{
        position: 'relative', display: 'flex', alignItems: 'center', gap: 11, width: '100%',
        height: 38, padding: '0 10px', borderRadius: 8, textAlign: 'left', cursor: 'pointer',
        fontSize: 13.5, fontWeight: active ? 600 : 500, letterSpacing: '-0.006em',
        color: active ? 'var(--accent-text)' : 'var(--text-2)',
        background: active ? 'var(--accent-soft)' : 'transparent',
        border: active ? RING : '1px solid transparent',
      }}>
      {active && <span style={{ position: 'absolute', left: 0, top: 8, bottom: 8, width: 3, borderRadius: '0 3px 3px 0', background: 'var(--accent)' }} />}
      <span className="sb-ico" style={{ flex: '0 0 auto', display: 'inline-flex', color: active ? 'var(--accent)' : 'var(--text-3)' }}>
        <SbIcon name={icon} size={17} />
      </span>
      <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      {badge > 0 && (
        <span className="mono" style={{
          flex: '0 0 auto', minWidth: 19, height: 18, padding: '0 5px', borderRadius: 999,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 10.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums', lineHeight: 1,
          color: active ? 'var(--accent-text)' : 'var(--text-3)',
          background: active ? 'color-mix(in oklch, var(--accent) 16%, transparent)' : 'var(--bg-surface-2)',
          border: active ? 'none' : '1px solid var(--border-subtle)',
        }}>{badge > 99 ? '99+' : badge}</span>
      )}
    </button>
  );
}

/* -------------------- group label / collapsed divider -------------------- */
function GroupLabel({ label, collapsed, first }) {
  if (collapsed) {
    return <div style={{ height: 1, margin: first ? '4px 8px 8px' : '12px 8px 8px', background: 'var(--border-subtle)' }} />;
  }
  return (
    <div style={{
      padding: '0 10px', marginTop: first ? 2 : 16, marginBottom: 5,
      fontSize: 10.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-4)',
    }}>{label}</div>
  );
}

/* -------------------- brand block -------------------- */
function Brand({ collapsed, onToggleCollapse, onNavigate }) {
  if (collapsed) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '2px 0 14px' }}>
        <button type="button" className="sb-brandbtn" onClick={() => onNavigate && onNavigate('/projects')} title="E3 Summit — Projects"
          style={{ padding: 3, borderRadius: 10, display: 'inline-flex', background: 'transparent', border: 'none', cursor: 'pointer' }}>
          <Logo size={30} radius={8} />
        </button>
        <button type="button" className="sb-iconbtn" onClick={onToggleCollapse} title="Expand sidebar"
          style={{ width: 30, height: 30, borderRadius: 8, color: 'var(--text-3)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <SbIcon name="chevrons-right" size={16} />
        </button>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 4px 14px' }}>
      <button type="button" className="sb-brandbtn" onClick={() => onNavigate && onNavigate('/projects')} title="E3 Summit — Projects"
        style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, padding: '4px 6px', margin: '-4px -6px', borderRadius: 9, textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer' }}>
        <Logo size={32} radius={9} />
        <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, gap: 1 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.015em', lineHeight: 1.1, whiteSpace: 'nowrap' }}>E3 Summit</span>
          <span style={{ fontSize: 9.5, fontWeight: 600, color: 'var(--text-4)', letterSpacing: '0.13em', whiteSpace: 'nowrap' }}>PROJECT TRACKING</span>
        </span>
      </button>
      <button type="button" className="sb-iconbtn" onClick={onToggleCollapse} title="Collapse sidebar"
        style={{ width: 28, height: 28, borderRadius: 7, flex: '0 0 auto', color: 'var(--text-4)', background: 'transparent', border: '1px solid transparent', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
        <SbIcon name="chevrons-left" size={16} />
      </button>
    </div>
  );
}

/* -------------------- user card -------------------- */
function IconBtn({ icon, label, danger, onClick }) {
  return (
    <button type="button" className={'sb-iconbtn' + (danger ? ' sb-danger' : '')} onClick={onClick} title={label} aria-label={label}
      style={{ width: 30, height: 30, borderRadius: 8, flex: '0 0 auto', color: 'var(--text-3)', background: 'transparent', border: '1px solid transparent', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
      <SbIcon name={icon} size={16} />
    </button>
  );
}

function UserCard({ user, collapsed, darkMode, onToggleDarkMode, onLogout }) {
  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'User';
  const role = ROLE_LABEL[user.role] || user.role || 'Member';
  const themeIcon = darkMode ? 'sun' : 'moon';
  const themeLabel = darkMode ? 'Switch to light mode' : 'Switch to dark mode';

  if (collapsed) {
    return (
      <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        <Avatar first={user.first_name} last={user.last_name} size={32} />
        <IconBtn icon={themeIcon} label={themeLabel} onClick={onToggleDarkMode} />
        <IconBtn icon="logout" label="Sign out" danger onClick={onLogout} />
      </div>
    );
  }
  const barBtn = (icon, label, danger, onClick) => (
    <button type="button" className={'sb-iconbtn' + (danger ? ' sb-danger' : '')} onClick={onClick} title={label} aria-label={label}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, flex: 1, height: 30, borderRadius: 7, fontSize: 11.5, fontWeight: 500, color: 'var(--text-3)', background: 'transparent', border: '1px solid transparent', cursor: 'pointer' }}>
      <SbIcon name={icon} size={15} />
      <span style={{ whiteSpace: 'nowrap' }}>{label}</span>
    </button>
  );
  return (
    <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
      <div style={{ padding: 7, borderRadius: 11, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-xs)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '3px 4px 9px' }}>
          <Avatar first={user.first_name} last={user.last_name} size={34} />
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1, gap: 1 }}>
            <span title={name} style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.006em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
            <span style={{ fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{role}</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, borderTop: '1px solid var(--border-subtle)', paddingTop: 7 }}>
          {barBtn(themeIcon, darkMode ? 'Light' : 'Dark', false, onToggleDarkMode)}
          <span style={{ width: 1, height: 16, background: 'var(--border-subtle)', flex: '0 0 auto' }} />
          {barBtn('logout', 'Sign out', true, onLogout)}
        </div>
      </div>
    </div>
  );
}

/* -------------------- project-detail mode (not mounted today; kept safe) ---- */
function BackLink({ collapsed, onNavigate }) {
  if (collapsed) {
    return (
      <button type="button" className="sb-iconbtn" onClick={() => onNavigate && onNavigate('/projects')} title="All projects"
        style={{ width: 40, height: 36, margin: '0 auto 6px', borderRadius: 9, color: 'var(--text-3)', background: 'transparent', border: '1px solid transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
        <SbIcon name="arrow-left" size={17} />
      </button>
    );
  }
  return (
    <button type="button" className="sb-back" onClick={() => onNavigate && onNavigate('/projects')}
      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', height: 32, padding: '0 8px', borderRadius: 8, color: 'var(--text-3)', fontSize: 12.5, fontWeight: 500, background: 'transparent', border: 'none', cursor: 'pointer', transition: 'background .11s, color .11s' }}>
      <SbIcon name="arrow-left" size={16} />
      <span style={{ whiteSpace: 'nowrap' }}>All projects</span>
    </button>
  );
}

/* -------------------- Sidebar -------------------- */
export default function Sidebar({
  sidebarNav = [],
  projectNav = [],
  isProjectDetail = false,
  currentProjectIdentifier = null,
  currentProjectTab = 'dashboard',
  pathname = '/projects',
  user = {},
  collapsed = false,
  onToggleCollapse,
  onNavigate,
  tabKeyToUrl = (k) => k,
  darkMode = false,
  onToggleDarkMode,
  onLogout,
  floating = true,
}) {
  ensureStyles();

  const W = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;
  const isActive = (path) =>
    path === '/projects' ? pathname === '/projects' || pathname === '/' : pathname === path;

  const byKey = Object.fromEntries(sidebarNav.map((it) => [it.key, it]));
  const grouped = new Set();
  NAV_GROUPS.forEach((g) => g.keys.forEach((k) => grouped.add(k)));
  const ungrouped = sidebarNav.filter((it) => !grouped.has(it.key)); // safety net for future items

  const floatStyle = floating
    ? { left: 14, top: 14, bottom: 14, borderRadius: 16, border: '1px solid var(--border-subtle)' }
    : { left: 0, top: 0, bottom: 0, borderRight: '1px solid var(--border-subtle)' };

  return (
    <aside className={'sb-rail' + (floating ? ' sb-rail-float' : '')} style={{
      position: 'fixed', zIndex: 150, width: W,
      display: 'flex', flexDirection: 'column',
      padding: collapsed ? '16px 8px 14px' : '18px 14px 14px',
      overflow: 'hidden', color: 'var(--text-2)', fontFamily: 'var(--font-sans)',
      transition: 'width 0.2s ease',
      ...floatStyle,
    }}>
      <Brand collapsed={collapsed} onToggleCollapse={onToggleCollapse} onNavigate={onNavigate} />

      {isProjectDetail ? (
        <>
          <BackLink collapsed={collapsed} onNavigate={onNavigate} />
          <div style={{ height: 1, background: 'var(--border-subtle)', margin: collapsed ? '2px 6px 12px' : '4px 0 12px' }} />
          {!collapsed && currentProjectIdentifier && typeof currentProjectIdentifier === 'object' && (
            <div style={{ padding: '0 10px', marginBottom: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.01em', lineHeight: 1.25 }}>{currentProjectIdentifier.name}</div>
              {currentProjectIdentifier.number && <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{currentProjectIdentifier.number}</div>}
            </div>
          )}
          <nav className="sb-nav" style={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', flex: 1, minHeight: 0 }}>
            {projectNav.map((tab) => (
              <NavItem key={tab.key} icon={TAB_ICON[tab.key] || 'dashboard'} label={tab.label}
                path={tabKeyToUrl(tab.key)} active={tab.key === currentProjectTab} collapsed={collapsed} onNavigate={onNavigate} />
            ))}
          </nav>
        </>
      ) : (
        <nav className="sb-nav" style={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {NAV_GROUPS.map((group, gi) => {
            const items = group.keys.map((k) => byKey[k]).filter(Boolean);
            if (items.length === 0) return null;
            return (
              <React.Fragment key={group.label}>
                <GroupLabel label={group.label} collapsed={collapsed} first={gi === 0} />
                {items.map((item) => (
                  <NavItem key={item.key} icon={NAV_ICON[item.key] || 'dashboard'} label={item.label}
                    path={item.path} active={isActive(item.path)} badge={item.badge} collapsed={collapsed} onNavigate={onNavigate} />
                ))}
              </React.Fragment>
            );
          })}
          {ungrouped.length > 0 && (
            <>
              <GroupLabel label="More" collapsed={collapsed} />
              {ungrouped.map((item) => (
                <NavItem key={item.key} icon={NAV_ICON[item.key] || 'dashboard'} label={item.label}
                  path={item.path} active={isActive(item.path)} badge={item.badge} collapsed={collapsed} onNavigate={onNavigate} />
              ))}
            </>
          )}
        </nav>
      )}

      <div style={{ height: 12, flex: '0 0 auto' }} />
      <UserCard user={user} collapsed={collapsed} darkMode={darkMode} onToggleDarkMode={onToggleDarkMode} onLogout={onLogout} />
    </aside>
  );
}
